import { computed, ref, type ComputedRef, type Ref } from 'vue';
import type { UIMessage } from 'ai';
import { load as yamlLoad, dump as yamlDump } from 'js-yaml';
import { findScreenById } from '../../lib/applyPatch';
import { getMessageText, parseMessageContent } from './messageUtils';

/** messageId → editIndex → pre-edit screen YAML. */
type CheckpointMap = Record<string, Record<number, string>>;

type Params = {
  /** Compiled course YAML (compiledYamlRef). */
  yaml: Ref<string | undefined> | ComputedRef<string | undefined>;
  /** Persisted messages — the reload source of truth for checkpoints. */
  initialMessages: () => any[] | undefined;
};

// Lite-mode pre-edit screen snapshots ("checkpoints"). Lite-only: full authoring
// never calls captureCheckpoints. Capture must run on streaming → ready, before
// the preview overlay mutates the buffer, so the snapshot is a clean pre-edit
// base for oldText matching.
export default function useLiteCheckpoints(params: Params) {
  /** Snapshots captured this session. The AI SDK strips custom metadata from
   * chat.messages, so without this they'd be invisible until persist+reload. */
  const liveCheckpoints = ref<CheckpointMap>({});

  function captureCheckpoints(message: UIMessage): void {
    const courseYaml = params.yaml.value;
    if (!courseYaml) return;

    let compiledObj: unknown;
    try {
      compiledObj = yamlLoad(courseYaml);
    } catch {
      return;
    }

    const chunks = parseMessageContent(getMessageText(message));
    for (const chunk of chunks) {
      if (!chunk.complete) continue;
      if (chunk.type !== 'edit' && chunk.type !== 'text-edit') continue;

      const editIndex = chunk.index;
      if (liveCheckpoints.value[message.id]?.[editIndex]) continue;

      try {
        const parsed = yamlLoad(chunk.content) as any;
        const screenId = chunk.type === 'edit' ? parsed?.id : parsed?.screenId;
        if (!screenId || typeof screenId !== 'string') continue;

        const screen = findScreenById(compiledObj as any, screenId);
        if (!screen) continue;

        const snapshot = yamlDump(screen, { lineWidth: -1 });
        const existing = liveCheckpoints.value[message.id] ?? {};
        liveCheckpoints.value = {
          ...liveCheckpoints.value,
          [message.id]: { ...existing, [editIndex]: snapshot }
        };
      } catch {
        // Unparseable chunk — resolution falls back to the live buffer.
      }
    }
  }

  // Persisted snapshots (initialMessages) win; live ones fill the session gap.
  // With no live snapshots (full authoring) this equals the legacy map.
  const checkpointsByMessageId = computed<CheckpointMap>(() => {
    const result: CheckpointMap = {};
    for (const [id, cps] of Object.entries(liveCheckpoints.value)) {
      result[id] = { ...cps };
    }
    for (const m of params.initialMessages() ?? []) {
      const id = (m as any)?.id;
      const checkpoints = (m as any)?.metadata?.checkpoints;
      if (id && checkpoints) result[id] = { ...result[id], ...checkpoints };
    }
    return result;
  });

  return { captureCheckpoints, checkpointsByMessageId, liveCheckpoints };
}
